export type MemoryCache<TCacheItem> = {
  get(key: string): TCacheItem | undefined;
  set(item: TCacheItem): void;
  remove(key: string): void;
  clear(): void;
  removeAll(prefix: string): void;
};

export function createCache<TCacheItem>(
  getKey: (item: TCacheItem) => string,
  maxSize: number,
): MemoryCache<TCacheItem> {
  if (!__BROWSER__) {
    return {
      get(key: string) {
        return undefined;
      },
      set(item: TCacheItem) {},
      remove(key: string) {},
      clear() {},
      removeAll() {},
    };
  }

  type Cache = { [key: string]: TCacheItem };
  let cache: Cache = {};
  let cacheKeys: string[] = [];

  function placeFirstInCache(key: string) {
    const existingIndex = cacheKeys.indexOf(key);
    if (existingIndex !== -1) {
      cacheKeys.splice(existingIndex, 1);
    }
    cacheKeys.push(key);
  }

  return {
    get(key: string): TCacheItem | undefined {
      const cacheItem = cache[key];
      if (cacheItem) {
        placeFirstInCache(key);
      }
      return cacheItem;
    },
    set(item: TCacheItem) {
      const key = getKey(item);
      cache[key] = item;
      placeFirstInCache(key);

      if (cacheKeys.length > maxSize) {
        const keysToRemove = cacheKeys.slice(0, cacheKeys.length - maxSize);
        cacheKeys = cacheKeys.slice(keysToRemove.length);

        for (const keyToRemove of keysToRemove) {
          delete cache[keyToRemove];
        }
      }
    },
    remove(key: string) {
      delete cache[key];
      const existingIndex = cacheKeys.indexOf(key);
      if (existingIndex !== -1) {
        cacheKeys.splice(existingIndex, 1);
      }
    },
    clear() {
      cache = {};
      cacheKeys = [];
    },
    removeAll(prefix: string) {
      cacheKeys.filter(key => key.startsWith(prefix)).forEach(key => this.remove(key));
    },
  };
}
